import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import api from '../../services/api';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';

const Page = styled.div`
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 16px;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'approved' ? '#2e7d3215' :
    $status === 'waiting' || $status === 'submitted' ? '#ed6c0215' :
    $status === 'rejected' ? '#d32f2f15' :
    $status === 'canceled' ? '#9e9e9e15' :
    '#0291EF15'};
  color: ${({ $status }) =>
    $status === 'approved' ? '#2e7d32' :
    $status === 'waiting' || $status === 'submitted' ? '#ed6c02' :
    $status === 'rejected' ? '#d32f2f' :
    $status === 'canceled' ? '#9e9e9e' :
    '#0291EF'};
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  color: #9ca3af;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const Value = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
`;

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TimelineItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  border-left: 3px solid ${({ $action }) =>
    $action === 'approved' ? '#2e7d32' :
    $action === 'rejected' ? '#d32f2f' :
    $action === 'submitted' ? '#0291EF' :
    '#9ca3af'};
  background: #f9fafb;
  border-radius: 4px;
`;

const TimelineHead = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
`;

const TimelineMeta = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
`;

const RejectOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RejectDialog = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const DocumentRow = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  margin-bottom: 8px;
  font-size: 13px;
  font-family: inherit;
  background: none;
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;

  &:hover { background: #f9fafb; }
`;

export default function BenefitApplicationDetailsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();

  const [item, setItem] = useState(null);
  const [history_, setHistoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  // { fileUrl, title } | null — açık olduğunda DocumentViewerModal'ı besler. `fileUrl` KAYITLI
  // depolama adresidir (artık özel sınıf — bkz. StoragePurposes.benefitDocument), imzalı adres
  // DEĞİL; çözümü DocumentViewerModal kendisi `/v1/storage/view` üzerinden yapar (beş
  // özlük-dosyası yüzeyiyle aynı desen, bkz. EmployeePersonnelFileTab.js).
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/v1/benefit-applications/${id}`),
      api.get(`/v1/benefit-applications/${id}/history`),
    ])
      .then(([detRes, histRes]) => {
        setItem(detRes.data?.data);
        setHistoryItems(histRes.data?.data || []);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = useCallback(async () => {
    setProcessing(true);
    try {
      await api.put(`/v1/benefit-applications/${id}/approve`);
      load();
    } finally {
      setProcessing(false);
    }
  }, [id, load]);

  const handleReject = useCallback(async () => {
    if (!rejectReason.trim()) return;
    setProcessing(true);
    try {
      await api.put(`/v1/benefit-applications/${id}/reject`, { rejectionReason: rejectReason.trim() });
      setRejectOpen(false);
      setRejectReason('');
      load();
    } finally {
      setProcessing(false);
    }
  }, [id, rejectReason, load]);

  const formatDateTime = (val) => {
    if (!val) return '-';
    const d = new Date(val);
    return `${d.toLocaleDateString('tr-TR')} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
  };

  if (loading) return null;
  if (!item) {
    return <Card>{t('benefitApplication.notFound')}</Card>;
  }

  const statusLabel = t(`benefitApplication.status${item.status?.charAt(0).toUpperCase()}${item.status?.slice(1)}`,
    { defaultValue: item.status });
  const typeLabel = t(`benefitApplication.type${item.applicationType?.split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('')}`,
    { defaultValue: item.applicationType });

  return (
    <Page>
      <Card>
        <Header>
          <div>
            <Title>{item.benefitName}</Title>
            <Subtitle>
              {item.employeeFullName} · {typeLabel}
            </Subtitle>
          </div>
          <StatusBadge $status={item.status}>{statusLabel}</StatusBadge>
        </Header>

        <Field>
          <Label>{t('benefitApplication.requestedActivationDate')}</Label>
          <Value>{item.requestedActivationDate ? new Date(item.requestedActivationDate).toLocaleDateString('tr-TR') : '-'}</Value>
        </Field>
        <Field>
          <Label>{t('benefitApplication.submittedDate')}</Label>
          <Value>{formatDateTime(item.createDate)}</Value>
        </Field>
        {item.relatedDependentName ? (
          <Field>
            <Label>{t('benefitApplication.relatedDependent')}</Label>
            <Value>{item.relatedDependentName}</Value>
          </Field>
        ) : null}
        {item.notes ? (
          <Field>
            <Label>{t('benefitApplication.notes')}</Label>
            <Value>{item.notes}</Value>
          </Field>
        ) : null}
        {item.rejectionReason ? (
          <Field>
            <Label>{t('benefitApplication.rejectionReason')}</Label>
            <Value style={{ color: '#d32f2f' }}>{item.rejectionReason}</Value>
          </Field>
        ) : null}
        {item.slaDeadline ? (
          <Field>
            <Label>{t('benefitApplication.slaDeadline')}</Label>
            <Value style={{ color: item.slaBreached ? '#d32f2f' : 'inherit' }}>
              {formatDateTime(item.slaDeadline)}
            </Value>
          </Field>
        ) : null}

        {item.documents?.length > 0 ? (
          <Field>
            <Label>{t('benefitApplication.documents')}</Label>
            {item.documents.map((d) => (
              <DocumentRow
                key={d.id}
                type="button"
                data-testid="benefit-document-row"
                onClick={() => setViewing({ fileUrl: d.mediaUrl, title: d.name || d.documentType })}
              >
                {d.name || d.documentType || d.mediaUrl}
              </DocumentRow>
            ))}
          </Field>
        ) : null}

        {item.canApprove && (item.status === 'waiting' || item.status === 'submitted') ? (
          <Actions>
            <Button onClick={handleApprove} disabled={processing}>
              {t('benefitApplication.approve')}
            </Button>
            <Button variant="outline" onClick={() => setRejectOpen(true)} disabled={processing}>
              {t('benefitApplication.reject')}
            </Button>
          </Actions>
        ) : null}
      </Card>

      <Card>
        <Title style={{ fontSize: 16, marginBottom: 16 }}>{t('benefitApplication.timeline')}</Title>
        {history_.length === 0 ? (
          <Subtitle>{t('benefitApplication.noHistory')}</Subtitle>
        ) : (
          <TimelineList>
            {history_.map((h) => (
              <TimelineItem key={h.id} $action={h.actionType}>
                <TimelineHead>
                  <span>{t(`benefitApplication.action${h.actionType?.charAt(0).toUpperCase()}${h.actionType?.slice(1).replace(/-(\w)/g, (_, c) => c.toUpperCase())}`,
                    { defaultValue: h.actionType })}</span>
                  <span style={{ fontWeight: 400, color: '#9ca3af' }}>{formatDateTime(h.createDate)}</span>
                </TimelineHead>
                <TimelineMeta>
                  {h.performedByUserName?.trim() || '-'}
                  {h.notes ? ` · ${h.notes}` : ''}
                </TimelineMeta>
              </TimelineItem>
            ))}
          </TimelineList>
        )}
      </Card>

      {rejectOpen && (
        <RejectOverlay onClick={() => !processing && setRejectOpen(false)}>
          <RejectDialog onClick={(e) => e.stopPropagation()}>
            <Title style={{ fontSize: 16, marginBottom: 14 }}>{t('benefitApplication.rejectTitle')}</Title>
            <TextInput
              label={t('benefitApplication.rejectReason')}
              type="textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              rows={3}
            />
            <Actions style={{ borderTop: 'none', justifyContent: 'flex-end', paddingTop: 8 }}>
              <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={processing}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleReject} disabled={processing || !rejectReason.trim()}
                style={{ background: '#d32f2f' }}>
                {processing ? '...' : t('benefitApplication.reject')}
              </Button>
            </Actions>
          </RejectDialog>
        </RejectOverlay>
      )}

      <DocumentViewerModal
        open={!!viewing}
        fileUrl={viewing?.fileUrl}
        title={viewing?.title}
        onClose={() => setViewing(null)}
      />
    </Page>
  );
}
